'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppList, useGroupList, useThreatConditions, useWebhookList } from '../../../../hooks';
import { useMemberList } from '../../../../hooks/useMemberList';
import { createThreatNotification, getThreatNotification, updateThreatNotification } from '../../../../services/manage/threatnotification';
import { RootState } from '../../../../store';
import { FormValues, ThreatNotification } from '../../../../type/manage';
import ContentLayout from '../../../Layout/Content';
import LoadMask from '../../../utils/loadmask';
import { showDialog, showToast } from '../../../utils/notifications';
import { ActionSection, ConditionSection, Header } from './sections';
import { FormTextField } from './ui';
import { getCommunicationChannelData, getDefaultFormValues, getFilteredAppList } from './utils/FormHelpers';
import { getCommunicationChannels } from './utils/formOptions';

const CreateThreat = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const isGroupManager = authContext?.is_group_manager;
    const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
        defaultValues: getDefaultFormValues(),
    });
    const platform = watch('os');
    const communicationChannel = watch('communication_channel');
    const { appListAll, loading: appListLoading, error: appListError } = useAppList(authContext, region, isGroupManager);
    const { groupList } = useGroupList(authContext, t);
    const { webhookList, refetch: refetchWebhookList } = useWebhookList(authContext, region);
    const { memberList } = useMemberList(authContext, region);
    const threatConditions = useThreatConditions(region, authContext, t, i18n);
    const isBetaAccount = authContext.is_beta_account;
    const isManager = authContext?.is_group_manager || authContext?.role === 'Owner' || authContext?.role === 'SubOwner';
    const [emails, setEmails] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [webhooks, setWebhooks] = useState<any[]>([]);
    const [ruleId, setRuleId] = useState<string>('');

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'ruleId') {
                setRuleId(value.ruleId?.toString() || '');
                if(value.ruleId !== '4'){
                    setValue('osVersion', '');
                }
                if(value.ruleId === '5'){
                    setValue('hackingType', '');
                }
            }
            if (name === 'communication_channel') {
                if (value.communication_channel === 'webhook') {
                    setValue('groups', groups?.length > 0 ? groups : []);
                    setValue('emails', emails?.length > 0 ? emails : []);
                } else if (value.communication_channel === 'email') {
                    setValue('slackIds', webhooks?.length > 0 ? webhooks : []);
                    setValue('groups', groups?.length > 0 ? groups : []);
                }else if(value.communication_channel === 'group'){
                    setValue('emails', emails?.length > 0 ? emails : []);
                    setValue('slackIds', webhooks?.length > 0 ? webhooks : []);
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue, groups, emails, webhooks]);

    
    useEffect(() => {
        if(!isManager) {
            showDialog({
                title: t('manage_threat_permission_dialog'),
                content: t('manage_threat_permission_dialog_description'),
                okText: t('ok'),
                onOk: () => {
                    navigate('/settings/notifications');
                },
                cancelButtonProps: {
                    hidden: true,
                },
            });
            return;
        }
        if (id) {
            const fetchThreatNotification = async () => {
                const response = await getThreatNotification(region, authContext.token, id);
                if (response?.result?.code === '0000') {
                    setValue('name', response.data.name);
                    setValue('ruleId', response.data.rule.ruleId);
                    setRuleId(response.data.rule.ruleId?.toString() || '');
                    setValue('os', response.data.os);
                    setValue('select_app', { package_name: response.data.packageName });
                    setValue('hackingType', response.data.hackingType);
                    setValue('osVersion', response.data.osVersion);
                    if (response.data.slacks && response.data.slacks.length > 0) {
                        const slackObjects = response.data.slacks.map((slack: any) => ({
                            value: slack,
                            label: slack,
                        }));
                        setValue('slackIds', slackObjects);
                        setValue('communication_channel', 'webhook');
                        setWebhooks(slackObjects);
                    }
                    if (response.data.groups && response.data.groups.length > 0) {
                        const groupObjects = response.data.groups.map((group: any) => ({
                            value: group.groupId,
                            label: group.groupName,
                        }));
                        setValue('groups', groupObjects);
                        setGroups(groupObjects);
                        setValue('communication_channel', 'group');
                    }
                    if (response.data.emails && response.data.emails.length > 0) {
                        const emailObjects = response.data.emails.map((email: any) => ({
                            value: email.email,
                            label: email.email,
                        }));
                        setValue('emails', emailObjects);
                        setEmails(emailObjects);
                        setValue('communication_channel', 'email');
                    }
                }
            };
            fetchThreatNotification();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateThreat = async (data: ThreatNotification) => {
        try {
            const response = await createThreatNotification(region, authContext.token, JSON.stringify(data));
            if (response?.result?.code === '0000') {
                showToast('success', t('created_threat_success'));
                navigate('/settings/threat');
            } else {
                showToast('error', t('created_threat_failed'));
            }
        } catch (error) {
            console.error('Submission error:', error);
        }
    };

    const handleUpdateThreat = async (data: ThreatNotification) => {
        try {
            const response = await updateThreatNotification(region, authContext.token, JSON.stringify(data), id!);
            if (response?.result?.code === '0000') {
                showToast('success', t('updated_threat_success'));
                navigate('/settings/threat');
            } else {
                showToast('error', t('updated_threat_failed'));
            }
        } catch (error) {
            console.error('Submission error:', error);
        }
    };

    const onSubmit = async (data: FormValues) => {
        try {
            const communicationData = getCommunicationChannelData(data);
            const params: ThreatNotification = {
                ruleId: data.ruleId,
                name: data.name,
                os: data.os,
                packageName: data.select_app.package_name,
                ...communicationData,
                hackingType: data.hackingType,
                osVersion: data.osVersion,
            };
            if (id) {
                handleUpdateThreat(params);
            } else {
                handleCreateThreat(params);
            }
        } catch (error) {
            console.error('Submission error:', error);
        }
    };

    const communicationChannels = getCommunicationChannels(t, isBetaAccount);
    const filteredAppList = getFilteredAppList(appListAll, platform);

    const groupOptions = groupList.map((group) => ({
        value: group.groupId,
        label: group.name,
    }));

    const webhookOptions = webhookList.map((webhook: any) => ({
        value: webhook.id,
        label: webhook.name,
    }));
    const memberOptions = memberList.map((member: any) => ({
        value: member.member_email,
        label: member.member_email,
    }));

    if (threatConditions.loading) {
        return (
            <ContentLayout fullWidth={true}>
                <LoadMask msg={t('validating_user_information')} />
            </ContentLayout>
        );
    }
    const sortOsVersion = (osVersions: any[]) => {
        const sortedOsVersions = osVersions.sort((a, b) => {
            const aIsIOS = a.value.startsWith('iOS');
            const bIsIOS = b.value.startsWith('iOS');
            
            if (aIsIOS && !bIsIOS) return -1;
            if (!aIsIOS && bIsIOS) return 1;
            
            const aMatch = a.value.match(/(\d+)/);
            const bMatch = b.value.match(/(\d+)/);
            
            if (!aMatch || !bMatch) return 0;
            
            const aVersion = parseInt(aMatch[1], 10);
            const bVersion = parseInt(bMatch[1], 10);
            
            return bVersion - aVersion;
        });
        return sortedOsVersions;
    };
    return (
        <ContentLayout fullWidth={true} position="relative" overflow="hidden">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Header t={t} navigate={navigate} isEditMode={!!id} />

                <div className="mt-10 space-y-10">
                    <div className="max-w-[496px]">
                        <FormTextField name="name" label={t('name')} control={control} placeholder={t('notification_name')} required />
                    </div>

                    <ConditionSection
                        control={control}
                        platform={platform}
                        appList={filteredAppList}
                        conditions={threatConditions.conditions}
                        hackingTypesForAndroid={threatConditions.hackingTypesForAndroid}
                        hackingTypesForIos={threatConditions.hackingTypesForIos}
                        osVersionsForAndroid={sortOsVersion(threatConditions.osVersionsForAndroid)}
                        osVersionsForIos={sortOsVersion(threatConditions.osVersionsForIos)}
                        t={t}
                        loading={appListLoading}
                        ruleId={ruleId}
                    />

                    <ActionSection
                        control={control}
                        isBetaAccount={isBetaAccount}
                        group={groupOptions}
                        communicationChannel={communicationChannel}
                        communicationChannels={communicationChannels}
                        webhookList={webhookOptions}
                        memberList={memberOptions}
                        refetchWebhookList={refetchWebhookList}
                        t={t}
                    />
                </div>
                {(appListError || threatConditions.error) && (
                    <div className="mt-4 text-red-500 text-sm">
                        {appListError && <div>App List Error: {appListError}</div>}
                        {threatConditions.error && <div>Conditions Error: {threatConditions.error}</div>}
                    </div>
                )}
            </form>
        </ContentLayout>
    );
};

export default CreateThreat;
